/**
 * Single source of truth for regional/country presets.
 * Used by Setup Wizard (onboarding) and Admin → Regional Settings.
 * Persisted to the backend `regional_settings` table (singleton row id=1).
 */
import i18n from '../i18n';

const baseFields = (overrides) => ({
  currencyDecimals: 2,
  currencySymbolBefore: true,
  taxIncludedInPrice: false,
  firstDayOfWeek: 1,
  ...overrides,
});

/** Full payloads saved to the API (no UI-only fields). */
export const REGION_PRESETS = {
  india: baseFields({
    label: 'India',
    countryCode: 'IN',
    countryName: 'India',
    timezone: 'Asia/Kolkata',
    localeTag: 'en-IN',
    currencyCode: 'INR',
    currencySymbol: '₹',
    taxLabel: 'GST',
    taxRatePercent: 18.0,
    taxIncludedInPrice: true,
    taxIdLabel: 'GSTIN',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd/MM/yyyy HH:mm',
    phoneDialCode: '+91',
    phoneFormatHint: '+91 XXXXX XXXXX',
  }),
  india12: baseFields({
    label: 'India (12-hour)',
    countryCode: 'IN',
    countryName: 'India',
    timezone: 'Asia/Kolkata',
    localeTag: 'en-IN',
    currencyCode: 'INR',
    currencySymbol: '₹',
    taxLabel: 'GST',
    taxRatePercent: 18.0,
    taxIncludedInPrice: true,
    taxIdLabel: 'GSTIN',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_12',
    datetimeFormat: 'dd/MM/yyyy hh:mm a',
    phoneDialCode: '+91',
    phoneFormatHint: '+91 XXXXX XXXXX',
  }),
  uae: baseFields({
    label: 'UAE',
    countryCode: 'AE',
    countryName: 'United Arab Emirates',
    timezone: 'Asia/Dubai',
    localeTag: 'en-AE',
    currencyCode: 'AED',
    currencySymbol: 'د.إ',
    taxLabel: 'VAT',
    taxRatePercent: 5.0,
    taxIdLabel: 'TRN',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_12',
    datetimeFormat: 'dd/MM/yyyy hh:mm a',
    firstDayOfWeek: 0,
    phoneDialCode: '+971',
    phoneFormatHint: '+971 XX XXX XXXX',
  }),
  us: baseFields({
    label: 'United States',
    countryCode: 'US',
    countryName: 'United States',
    timezone: 'America/New_York',
    localeTag: 'en-US',
    currencyCode: 'USD',
    currencySymbol: '$',
    taxLabel: 'Sales Tax',
    taxRatePercent: 0,
    taxIdLabel: '',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: 'HOUR_12',
    datetimeFormat: 'MM/dd/yyyy hh:mm a',
    firstDayOfWeek: 0,
    phoneDialCode: '+1',
    phoneFormatHint: '+1 (XXX) XXX-XXXX',
  }),
  canada: baseFields({
    label: 'Canada',
    countryCode: 'CA',
    countryName: 'Canada',
    timezone: 'America/Toronto',
    localeTag: 'en-CA',
    currencyCode: 'CAD',
    currencySymbol: '$',
    taxLabel: 'HST',
    taxRatePercent: 13.0,
    taxIdLabel: 'BN / HST #',
    dateFormat: 'yyyy-MM-dd',
    timeFormat: 'HOUR_12',
    datetimeFormat: 'yyyy-MM-dd hh:mm a',
    firstDayOfWeek: 0,
    phoneDialCode: '+1',
    phoneFormatHint: '+1 (XXX) XXX-XXXX',
  }),
  uk: baseFields({
    label: 'United Kingdom',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    timezone: 'Europe/London',
    localeTag: 'en-GB',
    currencyCode: 'GBP',
    currencySymbol: '£',
    taxLabel: 'VAT',
    taxRatePercent: 20.0,
    taxIdLabel: 'VAT Number',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd/MM/yyyy HH:mm',
    phoneDialCode: '+44',
    phoneFormatHint: '+44 XXXX XXXXXX',
  }),
  eurozone: baseFields({
    label: 'Eurozone (generic)',
    countryCode: 'DE',
    countryName: 'Germany',
    timezone: 'Europe/Berlin',
    localeTag: 'en-DE',
    currencyCode: 'EUR',
    currencySymbol: '€',
    taxLabel: 'VAT',
    taxRatePercent: 19.0,
    taxIdLabel: 'VAT Number',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd/MM/yyyy HH:mm',
    phoneDialCode: '+49',
    phoneFormatHint: '+49 XXX XXXXXXX',
  }),
  germany: baseFields({
    label: 'Germany',
    countryCode: 'DE',
    countryName: 'Germany',
    timezone: 'Europe/Berlin',
    localeTag: 'de-DE',
    currencyCode: 'EUR',
    currencySymbol: '€',
    currencySymbolBefore: false,
    taxLabel: 'MwSt.',
    taxRatePercent: 19.0,
    taxIdLabel: 'USt-IdNr.',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd.MM.yyyy HH:mm',
    phoneDialCode: '+49',
    phoneFormatHint: '+49 XXX XXXXXXX',
  }),
  france: baseFields({
    label: 'France',
    countryCode: 'FR',
    countryName: 'France',
    timezone: 'Europe/Paris',
    localeTag: 'fr-FR',
    currencyCode: 'EUR',
    currencySymbol: '€',
    currencySymbolBefore: false,
    taxLabel: 'TVA',
    taxRatePercent: 20.0,
    taxIdLabel: 'N° TVA',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd/MM/yyyy HH:mm',
    phoneDialCode: '+33',
    phoneFormatHint: '+33 X XX XX XX XX',
  }),
  netherlands: baseFields({
    label: 'Netherlands',
    countryCode: 'NL',
    countryName: 'Netherlands',
    timezone: 'Europe/Amsterdam',
    localeTag: 'nl-NL',
    currencyCode: 'EUR',
    currencySymbol: '€',
    currencySymbolBefore: false,
    taxLabel: 'BTW',
    taxRatePercent: 21.0,
    taxIdLabel: 'BTW-nummer',
    dateFormat: 'dd-MM-yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd-MM-yyyy HH:mm',
    phoneDialCode: '+31',
    phoneFormatHint: '+31 X XXXXXXXX',
  }),
  belgium: baseFields({
    label: 'Belgium',
    countryCode: 'BE',
    countryName: 'Belgium',
    timezone: 'Europe/Brussels',
    // Default UI/API locale for mixed-language BE academies; switch UI language separately.
    localeTag: 'fr-BE',
    currencyCode: 'EUR',
    currencySymbol: '€',
    currencySymbolBefore: false,
    taxLabel: 'TVA/BTW',
    taxRatePercent: 21.0,
    taxIdLabel: 'N° TVA / BTW-nummer',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd/MM/yyyy HH:mm',
    phoneDialCode: '+32',
    phoneFormatHint: '+32 XXX XX XX XX',
  }),
  spain: baseFields({
    label: 'Spain',
    countryCode: 'ES',
    countryName: 'Spain',
    timezone: 'Europe/Madrid',
    localeTag: 'es-ES',
    currencyCode: 'EUR',
    currencySymbol: '€',
    currencySymbolBefore: false,
    taxLabel: 'IVA',
    taxRatePercent: 21.0,
    taxIdLabel: 'NIF-IVA',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HOUR_24',
    datetimeFormat: 'dd/MM/yyyy HH:mm',
    phoneDialCode: '+34',
    phoneFormatHint: '+34 XXX XXX XXX',
  }),
};

/** Presets shown during first-time onboarding (Setup Wizard step 3). */
export const ONBOARDING_REGION_KEYS = [
  'india', 'uae', 'us', 'canada', 'uk', 'eurozone',
  'germany', 'france', 'netherlands', 'belgium', 'spain',
];

/** Localized dropdown label for a preset key. */
export function getRegionalPresetLabel(presetKey) {
  const key = `regional.presets.${presetKey}`;
  if (i18n.exists(key)) return i18n.t(key);
  return REGION_PRESETS[presetKey]?.label || presetKey;
}

/** Strip UI-only `label` before sending to the API. */
export function regionalPresetPayload(presetKey) {
  const preset = REGION_PRESETS[presetKey];
  if (!preset) return null;
  const { label, ...payload } = preset;
  return payload;
}

/**
 * Minimal fallback before onboarding saves regional_settings.
 * Intentionally NOT India-specific — real values come from the DB after setup.
 */
export const PRE_SETUP_FALLBACK = {
  countryCode: '',
  countryName: '',
  timezone: 'UTC',
  localeTag: 'en',
  enabledUiLanguages: ['en'],
  defaultUiLanguage: 'en',
  currencyCode: '',
  currencySymbol: '',
  currencyDecimals: 2,
  currencySymbolBefore: true,
  taxLabel: '',
  taxRatePercent: null,
  taxIncludedInPrice: false,
  taxIdLabel: '',
  taxRegistrationNumber: '',
  defaultPaymentMethod: 'CASH',
  paymentReferencePlaceholder: '',
  bankRoutingLabel: '',
  dateFormat: 'dd/MM/yyyy',
  timeFormat: 'HOUR_24',
  datetimeFormat: 'dd/MM/yyyy HH:mm',
  firstDayOfWeek: 1,
  phoneDialCode: '',
  phoneFormatHint: '',
};

function normalizeEnabledUiLanguagesField(raw) {
  const out = [];
  const seen = new Set();
  const push = (code) => {
    const c = String(code || '').trim().toLowerCase().slice(0, 2);
    if (!['en', 'de', 'fr', 'nl'].includes(c) || seen.has(c)) return;
    seen.add(c);
    out.push(c);
  };
  push('en');
  if (Array.isArray(raw)) raw.forEach(push);
  else if (typeof raw === 'string') raw.split(/[,\s]+/).forEach(push);
  return out.length ? out : ['en'];
}

/** Normalize public API response for RegionalSettingsContext. */
export function normalizeRegionalSettings(api = {}) {
  const enabledUiLanguages = normalizeEnabledUiLanguagesField(
    api.enabledUiLanguages ?? api.enabled_ui_languages,
  );
  let defaultUiLanguage = String(api.defaultUiLanguage || api.default_ui_language || 'en')
    .trim()
    .toLowerCase()
    .slice(0, 2);
  if (!enabledUiLanguages.includes(defaultUiLanguage)) {
    defaultUiLanguage = enabledUiLanguages[0] || 'en';
  }
  const hasConfiguredCurrency = Boolean(
    api.currencyCode && String(api.currencyCode).trim(),
  );
  if (!hasConfiguredCurrency) {
    return {
      ...PRE_SETUP_FALLBACK,
      ...api,
      enabledUiLanguages,
      defaultUiLanguage,
    };
  }
  return {
    currencyDecimals: 2,
    currencySymbolBefore: true,
    timeFormat: 'HOUR_24',
    firstDayOfWeek: 1,
    dateFormat: 'dd/MM/yyyy',
    datetimeFormat: 'dd/MM/yyyy HH:mm',
    taxIncludedInPrice: false,
    ...api,
    enabledUiLanguages,
    defaultUiLanguage,
  };
}
